import { create } from 'zustand';
import { api, setAccessToken } from './api.js';
import { setUserId } from './analytics.js';

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  locale: 'es' | 'en';
  /**
   * ISO-3166 alpha-2 detectado por IP al hacer signup. Puede ser null para
   * cuentas creadas antes de que existiera esta detección o cuando
   * Cloudflare no pudo resolver el país (T1, XX, etc.).
   */
  country: string | null;
  /** ISO 8601 string; null si el usuario no completó la verificación por correo. */
  emailVerifiedAt: string | null;
  isAdmin: boolean;
  memberships: Array<{ orgId: string; role: 'owner' | 'admin' | 'seller' | 'scanner' }>;
}

interface AuthState {
  user: SessionUser | null;
  hydrated: boolean;
  hydrate: () => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: SessionUser) => void;
  updateProfile: (input: { name?: string; locale?: 'es' | 'en' }) => Promise<SessionUser>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  hydrated: false,
  async hydrate() {
    try {
      const refresh = await api<{ accessToken: string }>('/auth/refresh', { method: 'POST' });
      setAccessToken(refresh.accessToken);
      const user = await api<SessionUser>('/auth/me');
      setUserId(user.id);
      set({ user, hydrated: true });
    } catch {
      setAccessToken(null);
      set({ user: null, hydrated: true });
    }
  },
  async logout() {
    await api('/auth/logout', { method: 'POST' }).catch(() => undefined);
    setAccessToken(null);
    setUserId(null);
    set({ user: null });
  },
  setUser(user) {
    set({ user });
  },
  async updateProfile(input) {
    const updated = await api<SessionUser>('/auth/me', {
      method: 'PATCH',
      body: input,
    });
    set({ user: updated });
    return updated;
  },
}));
